import { useEffect, useState, type FormEvent } from "react";
import { ClipboardCheck, Plus, Play, Save, X } from "lucide-react";
import { api, json } from "./api";
import { Empty, Modal, Spinner, dateLabel } from "./components";
import { FrozenScene } from "./History";
import {
  testStatuses,
  type Checkpoint,
  type CheckpointDetail,
  type Playtest,
} from "./historyTypes";
import type { Scene } from "./game/types";

export function Playtests({
  projectId,
  readOnly,
  scenes,
  initialSceneId,
  onInitialUsed,
}: {
  projectId: string;
  readOnly: boolean;
  scenes: Scene[];
  initialSceneId: string | null;
  onInitialUsed: () => void;
}) {
  const path = `/projects/${projectId}`,
    [tests, setTests] = useState<Playtest[]>([]),
    [versions, setVersions] = useState<Checkpoint[]>([]),
    [selected, setSelected] = useState<Playtest | null>(null),
    [snapshot, setSnapshot] = useState<CheckpointDetail | null>(null),
    [creating, setCreating] = useState(false),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [pendingScene, setPendingScene] = useState<string | null>(initialSceneId);
  const load = async () => {
    const [a, b] = await Promise.all([
      api<Playtest[]>(path + "/playtests"),
      api<Checkpoint[]>(path + "/checkpoints"),
    ]);
    setTests(a);
    setVersions(b);
  };
  useEffect(() => {
    let alive = true;
    Promise.all([
      api<Playtest[]>(path + "/playtests"),
      api<Checkpoint[]>(path + "/checkpoints"),
    ])
      .then(([a, b]) => {
        if (alive) {
          setTests(a);
          setVersions(b);
        }
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [path]);
  useEffect(() => {
    if (initialSceneId) {
      setPendingScene(initialSceneId);
      setCreating(true);
      onInitialUsed();
    }
  }, [initialSceneId, onInitialUsed]);
  async function open(test: Playtest) {
    setBusy(true);
    setError("");
    try {
      const c = await api<CheckpointDetail>(
        `${path}/checkpoints/${test.checkpointId}`,
      );
      setSnapshot(c);
      setSelected(test);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function save(next: Playtest) {
    setBusy(true);
    setError("");
    try {
      const saved = await api<Playtest>(
        `${path}/playtests/${next.id}`,
        json("PUT", next),
      );
      setSelected(saved);
      setTests((list) => list.map((t) => (t.id === saved.id ? saved : t)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="history-page playtests-page">
      <div className="history-heading">
        <div>
          <span className="eyebrow">ПРОВЕРКА ГИПОТЕЗ</span>
          <h1>Плейтесты</h1>
          <p className="muted">
            Каждая запись запускает именно ту версию прототипа, на которой
            проводилась проверка.
          </p>
        </div>
        <button
          className="primary"
          disabled={readOnly || !scenes.length}
          onClick={() => setCreating(true)}
        >
          <Plus size={16} />
          Новый плейтест
        </button>
      </div>
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <Spinner />
      ) : !tests.length ? (
        <Empty
          title="Запланируй первый плейтест"
          action={
            scenes.length ? (
              <button
                className="primary"
                disabled={readOnly}
                onClick={() => setCreating(true)}
              >
                Создать плейтест
              </button>
            ) : undefined
          }
        >
          Сформулируй гипотезу, выбери сцену и зафиксируй версию прототипа.
        </Empty>
      ) : (
        <div className="playtest-grid">
          {tests.map((t) => (
            <button
              className="playtest-card"
              key={t.id}
              onClick={() => void open(t)}
            >
              <div>
                <span className={`test-status ${t.status}`}>
                  {testStatuses[t.status]}
                </span>
                <small>{dateLabel(t.createdAt)}</small>
              </div>
              <h3>{t.title}</h3>
              <p>{t.hypothesis || "Гипотеза пока не сформулирована."}</p>
              <span>
                <Play size={14} />
                Открыть проверку
              </span>
            </button>
          ))}
        </div>
      )}
      {creating && (
        <CreateTest
          projectId={projectId}
          scenes={scenes}
          versions={versions}
          initialSceneId={pendingScene}
          busy={busy}
          error={error}
          close={() => {
            setCreating(false);
            setPendingScene(null);
          }}
          submit={async (input) => {
            setBusy(true);
            setError("");
            try {
              const t = await api<Playtest>(
                path + "/playtests",
                json("POST", input),
              );
              await load();
              setCreating(false);
              setPendingScene(null);
              await open(t);
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        />
      )}{" "}
      {selected && snapshot && (
        <div className="snapshot-overlay test-overlay">
          <header>
            <div>
              <span className="eyebrow">
                ПЛЕЙТЕСТ · {testStatuses[selected.status]}
              </span>
              <h2>{selected.title}</h2>
            </div>
            <button
              className="secondary"
              onClick={() => {
                setSelected(null);
                setSnapshot(null);
              }}
            >
              <X size={16} />
              Закрыть
            </button>
          </header>
          <div className="test-workspace">
            <section className="test-player">
              <FrozenScene
                snapshot={snapshot.snapshot}
                sceneId={selected.sceneId}
              />
            </section>
            <TestNotes
              key={`${selected.id}:${selected.version}`}
              value={selected}
              busy={busy}
              readOnly={readOnly}
              error={error}
              save={save}
            />
          </div>
        </div>
      )}
    </div>
  );
}
function CreateTest({
  projectId,
  scenes,
  versions,
  initialSceneId,
  busy,
  error,
  close,
  submit,
}: {
  projectId: string;
  scenes: Scene[];
  versions: Checkpoint[];
  initialSceneId: string | null;
  busy: boolean;
  error: string;
  close: () => void;
  submit: (v: unknown) => Promise<void>;
}) {
  const [title, setTitle] = useState(""),
    [hypothesis, setHypothesis] = useState(""),
    [sceneId, setSceneId] = useState(initialSceneId || scenes[0]?.id || ""),
    [checkpointId, setCheckpointId] = useState(""),
    [versionScenes, setVersionScenes] = useState<Scene[] | null>(null),
    [versionBusy, setVersionBusy] = useState(false);
  const choices = versionScenes || scenes;
  async function chooseVersion(value: string) {
    setCheckpointId(value);
    if (!value) {
      setVersionScenes(null);
      if (!scenes.some((scene) => scene.id === sceneId))
        setSceneId(scenes[0]?.id || "");
      return;
    }
    setVersionBusy(true);
    try {
      const detail = await api<CheckpointDetail>(
        `/projects/${projectId}/checkpoints/${value}`,
      );
      const available = detail.snapshot.scenes.filter(
        (scene) => !scene.deletedAt,
      );
      setVersionScenes(available);
      if (!available.some((scene) => scene.id === sceneId))
        setSceneId(available[0]?.id || "");
    } finally {
      setVersionBusy(false);
    }
  }
  return (
    <Modal title="Новый плейтест" close={close}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void submit({
            title,
            hypothesis,
            sceneId,
            checkpointId: checkpointId || undefined,
          });
        }}
      >
        <label>
          Название
          <input
            required
            autoFocus
            maxLength={160}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например, понятен ли рывок"
          />
        </label>
        <label>
          Гипотеза
          <textarea
            required
            maxLength={20000}
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            placeholder="Игрок поймёт, как преодолеть препятствие…"
          />
        </label>
        <label>
          Стартовая сцена
          <select value={sceneId} onChange={(e) => setSceneId(e.target.value)}>
            {choices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Версия прототипа
          <select
            value={checkpointId}
            onChange={(e) => void chooseVersion(e.target.value)}
          >
            <option value="">Зафиксировать текущее состояние</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.title} · {dateLabel(v.createdAt)}
              </option>
            ))}
          </select>
        </label>
        <p className="muted">
          {versionBusy
            ? "Открываем выбранную версию…"
            : "При выборе текущего состояния GDTool автоматически создаст неизменяемую контрольную версию."}
        </p>
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={close}
          >
            Отмена
          </button>
          <button
            className="primary"
            disabled={busy || !title.trim() || !hypothesis.trim() || !sceneId}
          >
            {busy ? <Spinner /> : <ClipboardCheck size={15} />}Создать и
            запустить
          </button>
        </div>
      </form>
    </Modal>
  );
}
function TestNotes({
  value,
  busy,
  readOnly,
  error,
  save,
}: {
  value: Playtest;
  busy: boolean;
  readOnly: boolean;
  error: string;
  save: (v: Playtest) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <aside className="test-notes">
      <span className="eyebrow">КАРТОЧКА ПРОВЕРКИ</span>
      <label>
        Статус
        <select
          disabled={readOnly}
          value={draft.status}
          onChange={(e) =>
            setDraft({ ...draft, status: e.target.value as Playtest["status"] })
          }
        >
          {Object.entries(testStatuses).map(([v, n]) => (
            <option key={v} value={v}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <label>
        Гипотеза
        <textarea
          disabled={readOnly}
          rows={4}
          maxLength={20000}
          value={draft.hypothesis}
          onChange={(e) => setDraft({ ...draft, hypothesis: e.target.value })}
        />
      </label>
      <label>
        Наблюдения
        <textarea
          disabled={readOnly}
          rows={7}
          maxLength={20000}
          value={draft.observations}
          onChange={(e) => setDraft({ ...draft, observations: e.target.value })}
          placeholder="Что делал игрок, где остановился, что сказал…"
        />
      </label>
      <label>
        Решение
        <textarea
          disabled={readOnly}
          rows={5}
          maxLength={20000}
          value={draft.decision}
          onChange={(e) => setDraft({ ...draft, decision: e.target.value })}
          placeholder="Что изменить или оставить…"
        />
      </label>
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      <button
        className="primary"
        disabled={readOnly || busy}
        onClick={() => void save(draft)}
      >
        {busy ? <Spinner /> : <Save size={15} />}Сохранить результат
      </button>
    </aside>
  );
}
